// Tracking → Carriers tab.
//
// Scorecard grid — one card per carrier. Compares operational quality
// across the carriers we ship through. Stats per card:
//   • Volume (shipped + delivered counts)
//   • Median + p90 transit, split Std vs Express
//   • Exception rate %
//   • Avg cost per shipment
//
// Carriers with too few delivered shipments to medium percentiles
// (< 5 samples) show "—" for those numbers rather than misleading
// values from a single outlier.

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import {
  Loader2, Package, Truck, AlertTriangle, DollarSign,
} from 'lucide-react'
import { getTrackingCarriers } from '../../lib/api'

function formatNum(v)   { return v == null ? '—' : new Intl.NumberFormat('en-AU').format(v) }
function formatDays(v)  { return v == null ? '—' : `${v.toFixed(1)}d` }
function formatPct(v)   { return v == null ? '—' : `${v.toFixed(2)}%` }
function formatAud(v)   {
  if (v == null) return '—'
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 2 }).format(v)
}

// Friendly names for the carrier slugs.
const CARRIER_LABEL = {
  'australia_post':                  'Australia Post — eParcel',
  'australia_post_mypost_business':  'Australia Post — MyPost Business',
  'startrack':                       'StarTrack',
  'fedex':                           'FedEx',
  'dhl_ecommerce_au':                'DHL eCommerce',
  'dhl_express_australia':           'DHL Express',
  'ups':                             'UPS',
  'aramex':                          'Aramex',
  'sendle':                          'Sendle',
  'tnt':                             'TNT',
}

function CarrierCard({ c }) {
  const label = CARRIER_LABEL[c.carrier] || c.carrier
  const t = c.transit_days
  // Visual cue for exception rate — anything > 1% is a red flag.
  const excpAccent = c.exception_rate_pct > 1 ? 'text-red-600' :
                     c.exception_rate_pct > 0 ? 'text-amber-600' : 'text-slate-600'
  return (
    <div className="card p-5">
      <div className="flex items-start justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-800 leading-tight">{label}</h3>
        <Truck size={16} className="text-slate-400 flex-shrink-0" />
      </div>

      {/* Volume row */}
      <div className="grid grid-cols-3 gap-3 mb-4 pb-4 border-b border-slate-100">
        <div>
          <p className="text-[10px] uppercase tracking-wider font-medium text-slate-400">Shipped</p>
          <p className="text-xl font-bold text-slate-700 tabular-nums">{formatNum(c.shipped)}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider font-medium text-slate-400">Delivered</p>
          <p className="text-xl font-bold text-emerald-600 tabular-nums">{formatNum(c.delivered)}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider font-medium text-slate-400">In-flight</p>
          <p className="text-xl font-bold text-blue-600 tabular-nums">{formatNum(c.in_flight)}</p>
        </div>
      </div>

      {/* Transit split */}
      <div className="grid grid-cols-2 gap-3 mb-4 pb-4 border-b border-slate-100">
        <div>
          <p className="text-[10px] uppercase tracking-wider font-medium text-slate-400 mb-1">Std median / p90</p>
          <p className="text-sm font-semibold text-slate-700 tabular-nums">
            {formatDays(t.standard.median)} <span className="text-slate-400 font-normal">/</span> {formatDays(t.standard.p90)}
          </p>
          <p className="text-[10px] text-slate-400 mt-0.5">n={formatNum(t.standard.count)}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider font-medium text-slate-400 mb-1">Exp median / p90</p>
          <p className="text-sm font-semibold text-slate-700 tabular-nums">
            {formatDays(t.express.median)} <span className="text-slate-400 font-normal">/</span> {formatDays(t.express.p90)}
          </p>
          <p className="text-[10px] text-slate-400 mt-0.5">n={formatNum(t.express.count)}</p>
        </div>
      </div>

      {/* Bottom row — exception rate + cost */}
      <div className="grid grid-cols-2 gap-3">
        <div className="flex items-start gap-2">
          <AlertTriangle size={14} className={`mt-0.5 ${excpAccent}`} />
          <div>
            <p className="text-[10px] uppercase tracking-wider font-medium text-slate-400">Exception rate</p>
            <p className={`text-sm font-semibold tabular-nums ${excpAccent}`}>{formatPct(c.exception_rate_pct)}</p>
            <p className="text-[10px] text-slate-400">{formatNum(c.exception_count)} affected</p>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <DollarSign size={14} className="text-slate-400 mt-0.5" />
          <div>
            <p className="text-[10px] uppercase tracking-wider font-medium text-slate-400">Avg cost</p>
            <p className="text-sm font-semibold text-slate-700 tabular-nums">{formatAud(c.avg_cost_aud)}</p>
            <p className="text-[10px] text-slate-400">per shipment</p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function TrackingCarriersTab() {
  const [days, setDays] = useState(30)
  const [params] = useSearchParams()
  const clientId = params.get('client_id') || undefined

  const { data, isLoading } = useQuery({
    queryKey: ['tracking', 'carriers', days, clientId],
    queryFn:  () => getTrackingCarriers({ days, client_id: clientId }),
    refetchInterval: 60_000,
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20 gap-2 text-slate-400">
        <Loader2 size={16} className="animate-spin" /> Loading carrier scorecards…
      </div>
    )
  }
  const carriers = data?.carriers || []

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-xs text-slate-500">
          {carriers.length} carriers with shipments in the last {days} days.
          Cards are sorted by volume (busiest first).
        </p>
        <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden bg-white text-xs">
          {[7, 30, 90].map(d => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={[
                'px-3 py-1.5 font-medium transition-colors',
                d === days ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50',
              ].join(' ')}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {carriers.length === 0 ? (
        <div className="card p-10 text-center text-slate-400">
          <Package size={24} className="mx-auto text-slate-300 mb-2" />
          <p className="text-sm">No carrier data in this window.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {carriers.map(c => <CarrierCard key={c.carrier} c={c} />)}
        </div>
      )}
    </div>
  )
}
